import React, { useMemo, useRef, useState } from 'react';
import { Form } from '@unform/web';
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import IconButton from '~/components/IconButton';
import InputSelect from '~/components/Inputs/InputSelect';
import Input from '~/components/Inputs/Input';
import { servicesTypes } from '~/util/servicesTypes';

export default function ExternalServiceCard({ service, index, handleSubmit }) {
  const formRefDesktop = useRef(null);
  const formRefMobile = useRef(null);

  const [editModeOn, setEditModeOn] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);

  const hasConfiguredKey = Boolean(service.has_key);

  const initialData = useMemo(
    () => ({
      key: '',
      uri: service.uri || '',
      service_type: service.service_type || '',
    }),
    [service]
  );

  const statusText = hasConfiguredKey
    ? service.masked_key || 'Credencial configurada'
    : 'Não configurada';

  const statusColor = hasConfiguredKey ? 'text-success' : 'text-muted';

  const resetForms = () => {
    formRefDesktop.current?.setErrors({});
    formRefMobile.current?.setErrors({});

    formRefDesktop.current?.setData(initialData);
    formRefMobile.current?.setData(initialData);
  };

  const openEditMode = () => {
    resetForms();
    setEditModeOn(true);
  };

  const closeEditMode = () => {
    resetForms();
    setEditModeOn(false);
  };

  const submitDesktopForm = () => {
    formRefDesktop.current?.submitForm();
  };

  const submitMobileForm = () => {
    formRefMobile.current?.submitForm();
  };

  const handleSave = data => {
    setEditModeOn(false);

    handleSubmit(
      {
        ...data,
        remove_key: false,
      },
      service,
      index
    );
  };

  const openRemoveModal = () => {
    setRemoveModalOpen(true);
  };

  const closeRemoveModal = () => {
    setRemoveModalOpen(false);
  };

  const confirmRemoveCredential = () => {
    setRemoveModalOpen(false);
    setEditModeOn(false);

    handleSubmit(
      {
        remove_key: true,
      },
      service,
      index
    );
  };

  const renderStatusBadge = () => (
    <div className={`font-size-12 font-weight-600 ${statusColor}`}>
      {statusText}
    </div>
  );

  const renderEditActions = submitHandler => (
    <Row className="w-100 mx-0 mt-3">
      <Col className="px-0 d-flex justify-content-end flex-wrap">
        <Button
          type="button"
          color="primary"
          outline
          className="new-layout-btn mr-2 mb-2"
          onClick={closeEditMode}
        >
          Cancelar
        </Button>

        {hasConfiguredKey && (
          <Button
            type="button"
            color="danger"
            outline
            className="new-layout-btn mr-2 mb-2"
            onClick={openRemoveModal}
          >
            Remover credencial
          </Button>
        )}

        <Button
          type="button"
          color="primary"
          className="new-layout-btn main mb-2"
          onClick={submitHandler}
        >
          Salvar
        </Button>
      </Col>
    </Row>
  );

  return (
    <>
      <Card
        className={`card new-layout table-card mb-4 ${
          service.border_class || ''
        } d-none d-lg-block`}
      >
        <CardBody className="card-body new-layout px-3 py-4">
          <Row className="w-100 mx-0 align-items-start">
            <Col xs={4} className="px-0 pr-3">
              <div className="new-layout-card-list-strong-value d-flex flex-wrap word-break-break-all">
                {service.name}
              </div>
              <div className="new-layout-card-list-value mt-1 text-muted font-size-12">
                {service.description}
              </div>
            </Col>

            <Col xs={2} className="px-0 pr-3">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Chave
              </div>
              {renderStatusBadge()}
            </Col>

            <Col xs={4} className="px-0 pr-3">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                URL
              </div>
              <div
                className="new-layout-card-list-value w-100 word-break-break-all"
                title={service.uri || 'Não informada'}
              >
                {service.uri || 'Não informada'}
              </div>
            </Col>

            <Col xs={1} className="px-0 pr-2">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Tipo
              </div>
              <div className="new-layout-card-list-value w-100 word-break-break-all">
                {service.service_type || 'Não informado'}
              </div>
            </Col>

            <Col xs={1} className="px-0 d-flex justify-content-end">
              {!editModeOn ? (
                <IconButton
                  icon="las la-pen"
                  color="secondary"
                  id={`edit-btn-${service.id}`}
                  tip="Editar"
                  click={openEditMode}
                />
              ) : (
                <IconButton
                  icon="las la-times"
                  color="danger"
                  id={`cancelar-btn-${service.id}`}
                  tip="Cancelar edição"
                  click={closeEditMode}
                />
              )}
            </Col>
          </Row>

          {editModeOn && (
            <div className="border-top mt-4 pt-4">
              <Form
                ref={formRefDesktop}
                onSubmit={handleSave}
                initialData={initialData}
                className="w-100"
              >
                <Row className="w-100 mx-0">
                  <Col lg={4} className="px-0 pr-lg-2">
                    <FormGroup>
                      <Label>Nova credencial</Label>
                      <Input name="key" placeholder="Nova credencial" />
                    </FormGroup>
                  </Col>

                  <Col lg={5} className="px-0 px-lg-2">
                    <FormGroup>
                      <Label>URL</Label>
                      <Input name="uri" placeholder="URL do serviço" />
                    </FormGroup>
                  </Col>

                  <Col lg={3} className="px-0 pl-lg-2">
                    <FormGroup>
                      <Label>Tipo de serviço</Label>
                      <InputSelect
                        name="service_type"
                        options={servicesTypes}
                        placeholder="Tipo de serviço"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {renderEditActions(submitDesktopForm)}

                <button
                  type="submit"
                  className="d-none"
                  aria-label="Salvar credencial desktop"
                />
              </Form>
            </div>
          )}
        </CardBody>
      </Card>

      <Card className="card new-layout list-card store d-lg-none mb-4">
        <CardBody className="card-body new-layout px-3 py-3">
          <Row className="w-100 mx-0 align-items-start">
            <Col xs={10} className="px-0">
              <div className="font-size-14 font-weight-700 text-gray-700">
                {service.name}
              </div>
              <div className="text-muted font-size-12 mt-1">
                {service.description}
              </div>
            </Col>

            <Col xs={2} className="px-0 d-flex justify-content-end">
              {!editModeOn ? (
                <IconButton
                  icon="las la-pen"
                  color="secondary"
                  id={`edit-btn-mobile-${service.id}`}
                  tip="Editar"
                  click={openEditMode}
                />
              ) : (
                <IconButton
                  icon="las la-times"
                  color="danger"
                  id={`cancelar-btn-mobile-${service.id}`}
                  tip="Cancelar edição"
                  click={closeEditMode}
                />
              )}
            </Col>
          </Row>

          <Row className="w-100 mx-0 mt-3">
            <Col xs={12} className="px-0 mb-2">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Chave
              </div>
              {renderStatusBadge()}
            </Col>

            <Col xs={12} className="px-0 mb-2">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                URL
              </div>
              <div className="font-size-12 text-gray-700 word-break-break-all">
                {service.uri || 'Não informada'}
              </div>
            </Col>

            <Col xs={12} className="px-0">
              <div className="font-size-11 font-weight-600 text-muted mb-1">
                Tipo de serviço
              </div>
              <div className="font-size-12 text-gray-700">
                {service.service_type || 'Não informado'}
              </div>
            </Col>
          </Row>

          {editModeOn && (
            <div className="border-top mt-3 pt-3">
              <Form
                ref={formRefMobile}
                onSubmit={handleSave}
                initialData={initialData}
              >
                <Row className="w-100 mx-0">
                  <Col xs={12} className="px-0 mb-2">
                    <FormGroup>
                      <Label>Nova credencial</Label>
                      <Input name="key" placeholder="Nova credencial" />
                    </FormGroup>
                  </Col>

                  <Col xs={12} className="px-0 mb-2">
                    <FormGroup>
                      <Label>URL</Label>
                      <Input name="uri" placeholder="URL do serviço" />
                    </FormGroup>
                  </Col>

                  <Col xs={12} className="px-0">
                    <FormGroup>
                      <Label>Tipo de serviço</Label>
                      <InputSelect
                        name="service_type"
                        options={servicesTypes}
                        placeholder="Tipo de serviço"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                {renderEditActions(submitMobileForm)}

                <button
                  type="submit"
                  className="d-none"
                  aria-label="Salvar credencial mobile"
                />
              </Form>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal isOpen={removeModalOpen} toggle={closeRemoveModal} centered>
        <ModalHeader toggle={closeRemoveModal}>Remover credencial</ModalHeader>

        <ModalBody>Deseja realmente remover esta credencial?</ModalBody>

        <ModalFooter>
          <Button
            type="button"
            color="primary"
            outline
            className="new-layout-btn"
            onClick={closeRemoveModal}
          >
            Cancelar
          </Button>

          <Button
            type="button"
            color="danger"
            className="new-layout-btn"
            onClick={confirmRemoveCredential}
          >
            Remover
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
